import { isRouteAction, isRouteActionType } from '@/router/utils';
import type { Middleware } from 'redux';

/**
 * Middleware to transparently handle action routing based on locale.
 * Will implicitly convert a route action to a locale route action if the locale is not `en`.
 */
export const localeRouteMiddleware: Middleware =
  ({ getState }) =>
  (next) =>
  (action) => {
    if (!isRouteAction(action)) {
      return next(action);
    }

    try {
      const state = getState();
      const currentLocale = state.location.payload?.locale || 'en';
      const newLocale = action?.payload?.locale ?? currentLocale;

      // Handle going from `en` locale to another locale.
      // The `en` locale is the default action as it has a route without the locale included in it.
      // When the locale is changed, we need to update the route to include the new locale.
      if (
        !action.type.includes('-LOCALE') &&
        newLocale !== 'en' &&
        // Ensure the action type we're trying to generate is a valid route action type.
        // Not all routes have a locale variant.
        isRouteActionType(`${action.type}-LOCALE`)
      ) {
        const transformedAction = {
          ...action,
          type: `${action.type}-LOCALE` as typeof action.type,
          payload: { ...action.payload, locale: newLocale },
        };
        // Not all Redux Router Actions include the full location object - e.g. redirects seem not to.
        if (action.meta?.location?.current !== undefined) {
          transformedAction.meta.location.current.pathname = `/${newLocale}${action.meta.location.current.pathname}`;
          transformedAction.meta.location.current.type = `${action.type}-LOCALE`;
        }
        return next(transformedAction);
      }

      // Handle going from a locale to the `en` locale.
      // In the reverse of the above, we need to swap from a locale route to the default route.
      if (
        !action.type.includes('-LOCALE') &&
        newLocale === 'en' &&
        // Ensure the action type we're trying to generate is a valid route action type.
        // Not all routes have a locale variant.
        isRouteActionType(`${action.type.replace('-LOCALE', '')}`)
      ) {
        const transformedAction = {
          ...action,
          type: `${action.type.replace('-LOCALE', '')}` as typeof action.type,
          payload: { ...action.payload, locale: newLocale },
        };
        // Not all Redux Router Actions include the full location object - e.g. redirects seem not to.
        if (action.meta?.location?.current !== undefined) {
          transformedAction.meta.location.current.pathname =
            transformedAction.meta.location.current.pathname.replace(
              `/${currentLocale}/`,
              '/'
            );
          transformedAction.meta.location.current.type = `${action.type.replace(
            '-LOCALE',
            ''
          )}`;
        }
      }
    } catch (error) {
      console.error('Error handling action in localeRoute middleware', error);
    }

    return next({ ...action });
  };
